import { useQuery } from "@tanstack/react-query"
import { api } from "../lib/api/client"
import type { MatterSearchResult, ContactSearchResult } from "../types"

interface GlobalSearchResult {
  matters: MatterSearchResult[]
  contacts: ContactSearchResult[]
}

/**
 * Hook to perform global search across matters and contacts.
 * Only triggers when searchString has 2+ characters.
 */
export function useGlobalSearch(searchString: string) {
  return useQuery({
    queryKey: ["globalSearch", searchString],
    queryFn: async (): Promise<GlobalSearchResult> => {
      if (!searchString || searchString.length < 2) {
        return { matters: [], contacts: [] }
      }

      const params = {
        searchString,
        limit: 10,
        filter: { deleted: false },
      }

      try {
        const [mattersResult, contactsResult] = await Promise.all([
          api.matters.search(params),
          api.contacts.search(params),
        ])

        return {
          matters: (mattersResult.data?.documents || []) as MatterSearchResult[],
          contacts: (contactsResult.data?.documents || []) as ContactSearchResult[],
        }
      } catch {
        return { matters: [], contacts: [] }
      }
    },
    enabled: searchString.length >= 2,
    staleTime: 1000 * 30, // 30 seconds
  })
}
